import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarClock } from "lucide-react";

const SELECT = "rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-200";
const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const FREQS = [["quotidien", "Tous les jours"], ["hebdomadaire", "Chaque semaine"], ["mensuel", "Chaque mois"], ["desactive", "Désactivé"]];

function summary(s) {
  if (s.frequency === "desactive") return "Aucun inventaire automatique.";
  const when = s.frequency === "quotidien" ? "chaque jour" : s.frequency === "hebdomadaire" ? `chaque ${WEEKDAYS[s.weekday].toLowerCase()}` : `le ${s.monthday} de chaque mois`;
  return `${s.count} article(s) tiré(s) au hasard ${when} à 8h.`;
}

export default function InventorySchedule({ isAdmin }) {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/inventory-schedule").then(({ data }) => { setS(data); setSaved(data); }).catch(() => {}); }, []);
  if (!s) return null;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const dirty = JSON.stringify(s) !== JSON.stringify(saved);
  const save = async () => {
    const count = Number(s.count);
    if (s.frequency !== "desactive" && (!Number.isInteger(count) || count < 1 || count > 100)) { toast.error("Le nombre d'articles doit être entre 1 et 100"); return; }
    setBusy(true);
    try { const { data } = await api.put("/inventory-schedule", { ...s, count: count || 1 }); setS(data); setSaved(data); toast.success("Planification enregistrée"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="inventory-schedule" className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <CalendarClock className="h-4 w-4 text-[#FF6A00]" />
        <p className="text-sm font-semibold text-white">Inventaire automatique</p>
        <span data-testid="inventory-schedule-summary" className="basis-full text-xs text-zinc-500 sm:ml-1 sm:basis-auto">{summary(saved)}</span>
      </div>
      {isAdmin && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs text-zinc-500">Fréquence<br />
            <select data-testid="inv-sched-frequency" value={s.frequency} onChange={(e) => set("frequency", e.target.value)} className={SELECT}>
              {FREQS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          {s.frequency === "hebdomadaire" && (
            <label className="text-xs text-zinc-500">Jour<br />
              <select data-testid="inv-sched-weekday" value={s.weekday} onChange={(e) => set("weekday", Number(e.target.value))} className={SELECT}>
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </label>
          )}
          {s.frequency === "mensuel" && (
            <label className="text-xs text-zinc-500">Jour du mois<br />
              <select data-testid="inv-sched-monthday" value={s.monthday} onChange={(e) => set("monthday", Number(e.target.value))} className={SELECT}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          {s.frequency !== "desactive" && (
            <label className="text-xs text-zinc-500">Nombre d'articles<br />
              <input data-testid="inv-sched-count" type="number" min={1} max={100} value={s.count} onChange={(e) => set("count", e.target.value)} className={`${SELECT} w-24`} />
            </label>
          )}
          <Button data-testid="inv-sched-save" disabled={!dirty || busy} onClick={save} className="rounded-full bg-[#FF6A00] px-4 text-sm font-semibold text-white hover:bg-[#E65F00]">Enregistrer</Button>
        </div>
      )}
    </div>
  );
}
